"use client";

import { useIsShopsite } from "@context/DomainContext";
import { XMarkIcon } from "@heroicons/react/24/solid";
import * as Toast from "@radix-ui/react-toast";
import { Button, Text } from "@components/theming";
import { useNotificationStore } from "@store/notificationStore";
import { joinClassNames } from "@utils/helpers";
import { useWindowSize } from "@utils/hooks";
import { useTranslations } from "next-intl";

const colorVariants: { [key: string]: string } = {
  emerald: "text-emerald-400",
  red: "text-red-400",
  orange: "text-orange-400",
};

// Notifications that come with a next step for the user to take (e.g. added
// to cart -> go to checkout). These stay open until the user dismisses them.
export default function ActionNotification() {
  const notificationTranslations = useTranslations("Notification");
  const windowSize = useWindowSize();
  const show = useNotificationStore((state) => state.actionShow);
  const setShow = useNotificationStore((state) => state.setActionShow);
  const content = useNotificationStore((state) => state.actionContent);
  const isShopsite = useIsShopsite();
  const isMobile = !!windowSize.width && windowSize.width <= 640;

  return (
    <Toast.Provider swipeDirection={isMobile ? "down" : "right"}>
      <Toast.Root
        open={show}
        onOpenChange={setShow}
        type="foreground"
        className={joinClassNames(
          "flex w-full p-4 shadow-lg pointer-events-auto backdrop-blur-popover ring-1 overflow-hidden",
          isMobile
            ? "data-[swipe=move]:transition-none transition-all data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)] data-[swipe=cancel]:translate-y-0 data-[swipe=end]:translate-y-[var(--radix-toast-swipe-end-y)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:slide-out-to-bottom-full data-[state=open]:slide-in-from-bottom-full"
            : "data-[swipe=move]:transition-none transition-all data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-top-full data-[state=closed]:slide-out-to-right-full",
          isShopsite
            ? isMobile
              ? "bg-black ring-white/10 rounded-t-2xl"
              : "bg-white ring-black ring-opacity-5 rounded-lg"
            : "bg-[color:var(--gray-2)] rounded-[var(--radius-3)]"
        )}
        duration={Infinity}
      >
        <Toast.Description className="flex flex-col w-full">
          <div className="flex items-center justify-between">
            <div className="flex">
              {content.icon ? (
                <content.icon.icon
                  aria-hidden="true"
                  className={joinClassNames(
                    `shrink-0 h-6 w-6 ${colorVariants[content.icon.color]}`
                  )}
                />
              ) : null}
              <div className="ml-3 pt-0.5">
                <Text
                  as="p"
                  className="text-sm font-medium"
                  shopsiteClasses={isMobile ? "text-white" : "text-black"}
                >
                  {content?.content}
                </Text>
              </div>
            </div>
            <Toast.Close
              className={joinClassNames(
                "inline-flex rounded-md h-min focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                isShopsite
                  ? isMobile
                    ? "text-gray-400 hover:text-gray-200 focus-visible:ring-indigo-500 focus-visible:ring-offset-black"
                    : "text-gray-400 hover:text-gray-500 focus-visible:ring-indigo-500 focus-visible:ring-offset-white"
                  : "text-[color:var(--gray-a11)] hover:text-[color:var(--gray-a9)] focus-visible:ring-[color:var(--accent-a9)]"
              )}
            >
              <span className="sr-only">
                {notificationTranslations("close")}
              </span>
              <XMarkIcon className="w-5 h-5" aria-hidden="true" />
            </Toast.Close>
          </div>
          <div className="flex flex-col mt-6 gap-4">
            <Button
              href="/cart"
              onClick={() => setShow(false)}
              sharedClasses="w-full"
              size="3"
            >
              {notificationTranslations("checkout")}
            </Button>
            {content?.extraContent && (
              <div
                className={isMobile && isShopsite ? "[&_p]:!text-white" : ""}
              >
                {content.extraContent}
              </div>
            )}
            <button
              className={
                isShopsite
                  ? isMobile
                    ? "text-gray-200 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                  : ""
              }
              onClick={() => setShow(false)}
            >
              {notificationTranslations("continueshopping")}
            </button>
          </div>
        </Toast.Description>
      </Toast.Root>
      <Toast.Viewport
        className={joinClassNames(
          "fixed z-[1000] outline-none flex flex-col",
          isMobile
            ? "bottom-0 left-0 w-full"
            : "top-20 right-0 sm:top-[5.5rem] w-96 max-w-[100vw] p-6 gap-3"
        )}
      />
    </Toast.Provider>
  );
}
